import React, { useEffect, useRef, useState } from 'react';

// --- THEME & CONFIGURATION ---
const THEME = {
  accent: '#F58220',      // Brand amber
  navy: '#090E1A',        // Deep navy/ink
  bg: '#FBFBFB',          // Off-white canvas
  slate200: '#E2E8F0',    // Grid lines
  slate300: '#CBD5E1',    // Default edges
  slate400: '#94A3B8',    // Default nodes
  slate600: '#475569',    // Text/Muted
  text: '#0F172A',        // Heading ink
};

const LABELS = [
  '// INSTITUTIONAL_BRAIN', '// PORTCO_FINANCIALS', 'CAP_TABLE_DATA',
  'MARKET_POSITION', 'FOUNDER_METRICS', '// EXIT_SCENARIOS', 'BOARD_MATERIALS', 'KPI_TRACKER'
];

// Interactive force-directed "institutional brain" graph (Brain product box).
export const BrainGraph = () => {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 440 });

  // Simulation State Refs (to avoid React re-renders on every frame)
  const graphRef = useRef({ nodes: [], links: [], particles: [] });
  const mouseRef = useRef({ x: -1000, y: -1000, active: false });

  // 1. Initialize Data
  useEffect(() => {
    const numNodes = 45; // Sparse, not dense
    const nodes = [];
    const links = [];

    // Create 4 Hubs
    for (let i = 0; i < 4; i++) {
      nodes.push({
        id: i,
        x: Math.random() * 600,
        y: Math.random() * 440,
        vx: 0, vy: 0,
        radius: i === 0 ? 16 : 10 + Math.random() * 4,
        type: i === 0 ? 'accent' : 'navy',
        label: LABELS[i % LABELS.length],
        isHub: true
      });
    }

    // Create Satellite Nodes
    for (let i = 4; i < numNodes; i++) {
      const hubId = i % 4;
      const isImportant = Math.random() > 0.8;
      nodes.push({
        id: i,
        x: Math.random() * 600,
        y: Math.random() * 440,
        vx: 0, vy: 0,
        radius: isImportant ? 6 + Math.random() * 4 : 3 + Math.random() * 3,
        type: isImportant ? (Math.random() > 0.5 ? 'accent' : 'navy') : 'slate',
        label: isImportant ? LABELS[(i + 3) % LABELS.length] : null,
        isHub: false
      });
      // Connect to a hub
      links.push({ source: i, target: hubId });

      // Randomly connect some nodes to each other to form a web, but keep it sparse
      if (Math.random() > 0.85) {
        links.push({ source: i, target: Math.floor(Math.random() * numNodes) });
      }
    }

    graphRef.current = { nodes, links, particles: [] };
  }, []);

  // 2. Physics & Animation Loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false });
    let animationFrameId;

    const render = () => {
      const { width, height } = dimensions;
      const { nodes, links, particles } = graphRef.current;
      const mouse = mouseRef.current;

      // --- PHYSICS ENGINE ---
      const centerX = width / 2;
      const centerY = height / 2;

      // Apply Forces
      for (let i = 0; i < nodes.length; i++) {
        let n1 = nodes[i];

        // 1. Center gravity (keeps graph on screen)
        n1.vx += (centerX - n1.x) * 0.0003;
        n1.vy += (centerY - n1.y) * 0.0003;

        // 2. Organic "living" wiggle
        n1.vx += (Math.random() - 0.5) * 0.1;
        n1.vy += (Math.random() - 0.5) * 0.1;

        // 3. Repulsion (Nodes push away from each other)
        for (let j = i + 1; j < nodes.length; j++) {
          let n2 = nodes[j];
          let dx = n1.x - n2.x;
          let dy = n1.y - n2.y;
          let distSq = dx * dx + dy * dy;
          if (distSq < 25000) { // Only repulse if relatively close
            let dist = Math.sqrt(distSq) || 1;
            let force = 150 / distSq;
            n1.vx += (dx / dist) * force;
            n1.vy += (dy / dist) * force;
            n2.vx -= (dx / dist) * force;
            n2.vy -= (dy / dist) * force;
          }
        }
      }

      // 4. Attraction (Links pull nodes together)
      links.forEach(link => {
        let source = typeof link.source === 'object' ? link.source : nodes[link.source];
        let target = typeof link.target === 'object' ? link.target : nodes[link.target];

        // Replace indices with object references on first run
        if (typeof link.source !== 'object') { link.source = source; link.target = target; }

        let dx = target.x - source.x;
        let dy = target.y - source.y;
        let dist = Math.sqrt(dx * dx + dy * dy) || 1;

        // Spring force
        const targetDist = source.isHub || target.isHub ? 100 : 40;
        let force = (dist - targetDist) * 0.002;

        source.vx += (dx / dist) * force;
        source.vy += (dy / dist) * force;
        target.vx -= (dx / dist) * force;
        target.vy -= (dy / dist) * force;
      });

      // Find Hovered Node
      let closestNode = null;
      let minHoverDist = 30;

      // Update Positions & Friction
      nodes.forEach(n => {
        n.vx *= 0.92; // Friction
        n.vy *= 0.92;
        n.x += n.vx;
        n.y += n.vy;

        // Keep nodes inside the canvas bounds
        const pad = n.radius + 4;
        if (n.x < pad) { n.x = pad; n.vx *= -0.5; }
        if (n.x > width - pad) { n.x = width - pad; n.vx *= -0.5; }
        if (n.y < pad) { n.y = pad; n.vy *= -0.5; }
        if (n.y > height - pad) { n.y = height - pad; n.vy *= -0.5; }

        // Check hover
        if (mouse.active) {
          let dx = mouse.x - n.x;
          let dy = mouse.y - n.y;
          let dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < minHoverDist) {
            minHoverDist = dist;
            closestNode = n;
          }
        }
      });

      // Update React state for UI overlay if hovered node changed
      if (closestNode?.id !== graphRef.current.hoveredId) {
        graphRef.current.hoveredId = closestNode?.id;
        setHoveredNode(closestNode);
      }

      // --- RENDER PIPELINE ---

      // 1. Background & Grid
      ctx.fillStyle = THEME.bg;
      ctx.fillRect(0, 0, width, height);

      ctx.strokeStyle = THEME.slate200;
      ctx.lineWidth = 1;
      const gridSize = 40;
      ctx.beginPath();
      for (let x = 0; x <= width; x += gridSize) { ctx.moveTo(x, 0); ctx.lineTo(x, height); }
      for (let y = 0; y <= height; y += gridSize) { ctx.moveTo(0, y); ctx.lineTo(width, y); }
      ctx.stroke();

      // Determine active components for highlights
      const activeNodeId = closestNode?.id;
      const activeLinkSet = new Set();
      if (activeNodeId != null) {
        links.forEach(l => {
          if (l.source.id === activeNodeId || l.target.id === activeNodeId) {
            activeLinkSet.add(l);
          }
        });
      }

      // 2. Draw Links
      links.forEach(link => {
        const isActive = activeNodeId != null ? activeLinkSet.has(link) : true;
        const isDimmed = activeNodeId != null && !isActive;

        ctx.beginPath();
        ctx.moveTo(link.source.x, link.source.y);
        ctx.lineTo(link.target.x, link.target.y);
        ctx.strokeStyle = isActive && activeNodeId != null ? THEME.accent : THEME.slate300;
        ctx.globalAlpha = isDimmed ? 0.15 : 0.6;
        ctx.lineWidth = isActive && activeNodeId != null ? 1.5 : 0.8;
        ctx.stroke();
      });
      ctx.globalAlpha = 1.0;

      // 3. Data Particles (Animation Flow)
      if (Math.random() > 0.8 && links.length > 0) {
        const link = links[Math.floor(Math.random() * links.length)];
        particles.push({
          link,
          progress: 0,
          speed: 0.005 + Math.random() * 0.015,
          color: Math.random() > 0.3 ? THEME.accent : THEME.navy
        });
      }

      // Update and draw particles
      for (let i = particles.length - 1; i >= 0; i--) {
        let p = particles[i];
        p.progress += p.speed;
        if (p.progress >= 1) {
          particles.splice(i, 1);
          continue;
        }

        const isDimmed = activeNodeId != null && !activeLinkSet.has(p.link);
        if (isDimmed) continue; // Hide unrelated particles during hover

        let x = p.link.source.x + (p.link.target.x - p.link.source.x) * p.progress;
        let y = p.link.source.y + (p.link.target.y - p.link.source.y) * p.progress;

        ctx.beginPath();
        ctx.arc(x, y, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 8;
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      // 4. Draw Nodes
      nodes.forEach(n => {
        const isHovered = activeNodeId === n.id;
        const isConnected = activeNodeId != null && Array.from(activeLinkSet).some(l => l.source.id === n.id || l.target.id === n.id);
        const isDimmed = activeNodeId != null && !isHovered && !isConnected;

        ctx.globalAlpha = isDimmed ? 0.2 : 1.0;

        // Glow effect for active/hubs
        if (isHovered || (n.type === 'accent' && !isDimmed)) {
          ctx.shadowColor = n.type === 'accent' || isHovered ? THEME.accent : THEME.navy;
          ctx.shadowBlur = isHovered ? 20 : 10;
        } else {
          ctx.shadowBlur = 0;
        }

        ctx.beginPath();
        ctx.arc(n.x, n.y, isHovered ? n.radius + 2 : n.radius, 0, Math.PI * 2);

        if (n.type === 'accent') ctx.fillStyle = THEME.accent;
        else if (n.type === 'navy') ctx.fillStyle = THEME.navy;
        else ctx.fillStyle = THEME.slate400;

        ctx.fill();

        // Node borders
        ctx.lineWidth = 2;
        ctx.strokeStyle = THEME.bg;
        ctx.stroke();
        ctx.shadowBlur = 0;

        // Draw Labels
        if ((n.label && !isDimmed) || isHovered) {
          ctx.font = '10px "JetBrains Mono", monospace';
          ctx.fillStyle = isHovered ? THEME.text : THEME.slate600;
          ctx.letterSpacing = '1px';
          ctx.fillText(n.label || `// NODE_${n.id}`, n.x + n.radius + 8, n.y + 4);
        }
      });
      ctx.globalAlpha = 1.0;

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [dimensions]);

  // 3. Event Listeners for Resize
  useEffect(() => {
    const handleResize = () => {
      if (wrapperRef.current) {
        setDimensions({
          width: wrapperRef.current.offsetWidth,
          height: wrapperRef.current.offsetHeight
        });
      }
    };

    handleResize(); // Initial measurement
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    mouseRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      active: true
    };
  };

  const handleMouseLeave = () => {
    mouseRef.current.active = false;
    if (hoveredNode) {
      graphRef.current.hoveredId = null;
      setHoveredNode(null);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="relative w-full max-w-[620px] h-[340px] sm:h-[380px] overflow-hidden rounded-2xl border border-stone-200 bg-[#FBFBFB]"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Interactive Canvas */}
      <canvas
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="block cursor-crosshair"
      />

      {/* Dynamic Tooltip for Hovered Node */}
      {hoveredNode && (
        <div
          className="absolute z-20 pointer-events-none bg-white/90 backdrop-blur-sm border border-slate-200 shadow-xl rounded-xl p-4 transition-all duration-150 ease-out"
          style={{
            left: Math.min(mouseRef.current.x + 15, dimensions.width - 180),
            top: Math.min(mouseRef.current.y + 15, dimensions.height - 90),
          }}
        >
          <div
            className="text-[9px] uppercase tracking-widest mb-1"
            style={{ color: THEME.accent, fontFamily: "'JetBrains Mono', monospace" }}
          >
            {hoveredNode.isHub ? '// BRAIN_HUB' : '// PORTCO_NODE'}
          </div>
          <div className="text-sm font-bold text-[#090E1A] mb-1">
            {hoveredNode.label || `Portco_Asset_${hoveredNode.id}`}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
            <span className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: THEME.accent }}></div>
              Active
            </span>
            <span>&middot;</span>
            <span>ID: {hoveredNode.id.toString().padStart(4, '0')}</span>
          </div>
        </div>
      )}
    </div>
  );
};
